import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Music, Upload, BarChart3, Users, Radio, TrendingUp, Plus, Settings, Play, Crown, Loader2, Library, Eye, Flame, Zap, Rocket, ArrowUpRight, ArrowDownRight, Video } from 'lucide-react';
import TrackUploadForm from '../../components/TrackUploadForm';
import VideoUploadForm from '../../components/VideoUploadForm';
import VideoManager from '../../components/VideoManager';
import { PostComposer } from '../../components/PostComposer';
import MyMusicManager from '../../components/MyMusicManager';
import { API } from '../../config/api';
import { getProfileUrl } from '../../utils/profileUtils';
import { safeJson } from '../../utils/safeJson';



export default function ArtistDashboard() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders, artistTier, hasActiveSubscription, bypassBilling, isOwner } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ plays: 0, followers: 0, tracks: 0 });
  const [tracks, setTracks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [artistProfile, setArtistProfile] = useState(null);
  const [genres, setGenres] = useState([]);
  const [momentum, setMomentum] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Check role - allow both artist and owner roles
    if (user?.role && user.role !== 'artist' && user.role !== 'owner') {
      // Navigate to appropriate dashboard for other roles
      navigate(`/dashboard/${user.role}`);
      return;
    }
    
    // Check subscription for artists (owners bypass this)
    if (user?.role === 'artist' && !hasActiveSubscription && !bypassBilling && !isOwner) {
      toast.info('Please subscribe to access artist features');
      navigate('/pricing');
      return;
    }
    
    fetchDashboardData();
  }, [isAuthenticated, authLoading, user, hasActiveSubscription]);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      
      const [profileRes, tracksRes, statsRes, genresRes, momentumRes, videosRes] = await Promise.all([
        fetch(`${API}/artist/me`, { headers }).catch(() => null),
        fetch(`${API}/artist/tracks`, { headers }).catch(() => null),
        fetch(`${API}/artist/stats`, { headers }).catch(() => null),
        fetch(`${API}/reference/genres`).catch(() => null),
        fetch(`${API}/creator/momentum`, { headers }).catch(() => null),
        fetch(`${API}/artist/videos`, { headers }).catch(() => null),
      ]);

      if (profileRes?.ok) {
        setArtistProfile(await profileRes.json());
      }
      
      if (tracksRes?.ok) {
        const data = await tracksRes.json();
        setTracks(data.tracks || []);
      }
      
      if (statsRes?.ok) {
        setStats(await statsRes.json());
      }
      
      if (genresRes?.ok) {
        const data = await genresRes.json();
        setGenres(data.genres || []);
      }
      
      if (videosRes?.ok) {
        const data = await videosRes.json();
        setVideos(data.videos || []);
      }
      
      if (momentumRes?.ok) {
        setMomentum(await momentumRes.json());
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = () => {
    const tier = artistTier || 'starter';
    const colors = {
      starter: 'bg-gray-500/20 text-gray-400',
      growth: 'bg-green-500/20 text-green-400',
      pro: 'bg-blue-500/20 text-blue-400',
      elite: 'bg-purple-500/20 text-purple-400',
      executive: 'bg-amber-500/20 text-amber-400',
      label: 'bg-secondary/20 text-secondary'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs ${colors[tier] || colors.starter}`}>
        {tier.charAt(0).toUpperCase() + tier.slice(1)}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="artist-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
              <Music className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl font-bold">{artistProfile?.stage_name || user?.name || 'Artist'}</h1>
                {getTierBadge()}
              </div>
              <p className="text-muted-foreground">@{user?.handle || 'handle'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/settings')}>
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button onClick={() => {
              if (!user?.handle) {
                toast.error('Please set your @handle first', {
                  description: 'Go to Settings to claim your unique handle',
                  action: {
                    label: 'Settings',
                    onClick: () => navigate('/settings')
                  }
                });
                return;
              }
              navigate(getProfileUrl(user));
            }}>
              View Public Profile
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <Play className="w-8 h-8 text-secondary" />
              <div>
                <p className="text-2xl font-bold">{stats.plays?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Total Plays</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold">{stats.followers?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Followers</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <Music className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-2xl font-bold">{tracks.length}</p>
                <p className="text-xs text-muted-foreground">Tracks</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold">{stats.chart_position || '--'}</p>
                <p className="text-xs text-muted-foreground">Chart Position</p>
              </div>
            </div>
          </Card>
          <Card 
            className="glass-card border-border p-4 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => navigate('/dashboard/requests')}
            data-testid="requests-stat-card"
          >
            <div className="flex items-center gap-3">
              <Radio className="w-8 h-8 text-orange-400" />
              <div>
                <p className="text-2xl font-bold">{stats.requests_count?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Requests</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4" data-testid="profile-views-stat-card">
            <div className="flex items-center gap-3">
              <Eye className="w-8 h-8 text-cyan-400" />
              <div>
                <p className="text-2xl font-bold">{stats.profile_views?.toLocaleString() || 0}</p>
                <p className="text-xs text-muted-foreground">Profile Views</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="upload" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
            <TabsTrigger value="upload" data-testid="artist-upload-tab">
              <Upload className="w-4 h-4 mr-2" /> Upload
            </TabsTrigger>
            <TabsTrigger value="posts" data-testid="artist-posts-tab">
              <Plus className="w-4 h-4 mr-2" /> Post
            </TabsTrigger>
            <TabsTrigger value="videos" data-testid="artist-videos-tab">
              <Video className="w-4 h-4 mr-2" /> Videos
            </TabsTrigger>
            <TabsTrigger value="music" data-testid="artist-music-tab">
              <Music className="w-4 h-4 mr-2" /> Music
            </TabsTrigger>
            <TabsTrigger value="stats" data-testid="artist-stats-tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Stats
            </TabsTrigger>
            <TabsTrigger value="battles" data-testid="artist-battles-tab">
              <Crown className="w-4 h-4 mr-2" /> Battles
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading text-lg font-bold mb-4">Upload Track</h3>
                <TrackUploadForm onSuccess={fetchDashboardData} getAuthHeaders={getAuthHeaders} genres={genres} />
              </Card>
              <Card className="glass-card border-border p-6">
                <h3 className="font-heading text-lg font-bold mb-4">Upload Video</h3>
                <VideoUploadForm onSuccess={fetchDashboardData} getAuthHeaders={getAuthHeaders} tracks={tracks} />
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="posts">
            <Card className="glass-card border-border p-6">
              <h3 className="font-heading text-lg font-bold mb-4">Create Post</h3>
              <PostComposer onPostCreated={fetchDashboardData} />
            </Card>
          </TabsContent>

          <TabsContent value="videos">
            <VideoManager
              videos={videos}
              onRefresh={fetchDashboardData}
              getAuthHeaders={getAuthHeaders}
              tracks={tracks}
              artistProfile={artistProfile}
              user={user}
            />
          </TabsContent>

          <TabsContent value="music">
            <Card className="glass-card border-border p-6">
              <MyMusicManager />
            </Card>
          </TabsContent>

          <TabsContent value="stats">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Momentum Overview */}
              <Card className="glass-card border-border p-6" data-testid="momentum-overview">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-violet-400" /> Your Momentum
                </h3>
                {momentum?.message && (
                  <div className="p-3 rounded-xl bg-gradient-to-r from-violet-600/10 to-amber-600/10 border border-violet-500/20 mb-4">
                    <p className="text-sm font-semibold text-foreground">{momentum.message}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Views Today</span>
                      {momentum?.growth_pct !== 0 && (
                        <span className={`text-[11px] font-bold flex items-center gap-0.5 ${(momentum?.growth_pct || 0) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {(momentum?.growth_pct || 0) > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {Math.abs(momentum?.growth_pct || 0)}%
                        </span>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-foreground">{(momentum?.today_views || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">vs {(momentum?.yesterday_views || 0).toLocaleString()} yesterday</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <span className="text-xs text-muted-foreground">Reactions Today</span>
                    <p className="text-2xl font-bold text-foreground mt-1">{(momentum?.today_reactions || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">on your content</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <span className="text-xs text-muted-foreground">New Followers</span>
                    <p className="text-2xl font-bold text-foreground mt-1">+{momentum?.week_followers || 0}</p>
                    <p className="text-[10px] text-muted-foreground">this week</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <span className="text-xs text-muted-foreground">Trending Posts</span>
                    <p className="text-2xl font-bold text-foreground mt-1">{momentum?.trending_posts || 0}</p>
                    <p className="text-[10px] text-muted-foreground">gaining traction</p>
                  </div>
                </div>
              </Card>

              {/* Quick Actions */}
              <Card className="glass-card border-border p-6" data-testid="growth-actions">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-amber-400" /> Grow Your Reach
                </h3>
                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/viral-engine')}>
                    <Flame className="w-5 h-5 text-orange-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Viral Engine</p>
                      <p className="text-[11px] text-muted-foreground">AI-powered growth suggestions</p>
                    </div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/analytics')}>
                    <BarChart3 className="w-5 h-5 text-violet-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Full Analytics</p>
                      <p className="text-[11px] text-muted-foreground">Deep dive into your performance</p>
                    </div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/creator/earnings')}>
                    <Zap className="w-5 h-5 text-green-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Earnings Dashboard</p>
                      <p className="text-[11px] text-muted-foreground">Track your revenue</p>
                    </div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/post/create')}>
                    <Plus className="w-5 h-5 text-cyan-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Create Content</p>
                      <p className="text-[11px] text-muted-foreground">Post to stay in the algorithm</p>
                    </div>
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="battles">
            <Card className="glass-card border-border p-6">
              <h3 className="font-heading text-lg font-bold mb-4">Battles & Cyphers</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Button variant="outline" className="h-24" onClick={() => navigate('/battles')}>
                  <div className="text-center">
                    <Crown className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                    <span>Join a Battle</span>
                  </div>
                </Button>
                <Button variant="outline" className="h-24" onClick={() => navigate('/battle-bot')}>
                  <div className="text-center">
                    <Radio className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                    <span>Battle the Bot</span>
                  </div>
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
